<template>
    <div class="user-wrap font-5"
         v-if="userName"
         @click="showUserNameMenu = !showUserNameMenu"
         :class="{'mobile': isMobile}"
         v-iron-source-click-outside="{
            handler: 'handleClickOutside',
            exclude: ['userMain']
         }"
         ref="userMain" id="userMenu">
        <span @mouseenter="userIconHover = true"
              @mouseleave="userIconHover = false"
              class="user-icon">
            <IconBase iconName="userIcon"
                      :iconColor="userIconColor"
                      :width="userIconWidth"
                      :height="userIconHeight"
            ></IconBase>
        </span>
        <span class="user-name">{{userName}}</span>
        <span class="small-triangle"></span>
        <div v-show="showUserNameMenu || isMobile" class="menu-container"
             @mouseenter="resetTimerForDelay"
             @mouseleave="delayCloseMenu"
        >
            <span class="user-name in-menu-user-name font-4">{{userName}}</span>
            <div v-for="(group, groupIndex) in userMenu" :key="groupIndex" class="user-menu-item-list">
                <div v-for="item in group" :key="item.id">
                    <span class="menu-item" @click="$emit('itemClicked', item.id)">
                        <GeneralLink :path="item.path"
                                     :isExternal="item.externalRoute"
                                     :openInNewTab="item.openInNewTab">
                            <IconBase :iconName="item.icon"
                                      :iconColor="consts.COLORS.WHITE"
                                      :width="item.iconWidth"
                                      :height="item.iconHeight"
                                      v-if="isMobile"
                                      :class="item.iconClass"
                            ></IconBase>
                            <div class="menu-name"
                                 :class="{'shown-user-name': showItemOnMobile(item)}">
                                <div>{{ item.name }}</div>
                                <span v-if="showItemOnMobile(item)" class="user-name">{{userName}}</span>
                            </div>
                        </GeneralLink>
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ironSourceClickOutside from 'directives/IronSourceClickOutside';
import IconBase from 'components/IconBase.vue';
import consts from 'services/metaData/consts';
import { getContactUsUrl } from 'services/metaData/links';
import userDataService from 'services/userData';
import authService from 'services/auth';
import GeneralLink from 'components/layout/GeneralLink.vue';
import { mapGetters } from 'vuex';
import { userMenuFullList } from './consts';

export default {
    name: 'User',
    components: { GeneralLink, IconBase },
    directives: { ironSourceClickOutside },
    data() {
        return {
            showUserNameMenu: false,
            consts,
            userName: null,
            userIconHover: false,
            userMenu: [],
            contactUsUrl: null,
            currentUserId: null,
            timeout: null
        };
    },
    props: {
        mode: {
            type: String,
            default: 'desktop'
        }
    },
    computed: {
        isMobile() {
            return this.mode === 'mobile';
        },
        userIconColor() {
            const isMediumAndSmallScreens = this.windowWidth < consts.RESPONSIVE_WIDTH_STEPS.LARGE_LAPTOP;
            if (isMediumAndSmallScreens) {
                if (this.showUserNameMenu || this.userIconHover) {
                    return consts.COLORS.PRIMARY_500;
                }
                return consts.COLORS.LIGHT_800;
            }
            return consts.COLORS.WHITE;
        },
        userIconWidth() {
            const isMediumAndSmallScreens = this.windowWidth < consts.RESPONSIVE_WIDTH_STEPS.LARGE_LAPTOP;
            return isMediumAndSmallScreens ? '18px' : '14px';
        },
        userIconHeight() {
            const isMediumAndSmallScreens = this.windowWidth < consts.RESPONSIVE_WIDTH_STEPS.LARGE_LAPTOP;
            return isMediumAndSmallScreens ? '18px' : '14px';
        },
        ...mapGetters([
            'windowWidth'
        ])
    },
    methods: {
        handleClickOutside() {
            this.showUserNameMenu = false;
        },
        showItemOnMobile(item) {
            return item.showUserNameInMobileMode && this.isMobile;
        },
        async init(userData) {
            this.userName = userData.userName;
            const allMenuItems = userMenuFullList.flat();

            // check uer permission and disable some elements
            if (userData.permissions) {
                await Promise.all(allMenuItems.map(async (item) => {
                    // eslint-disable-next-line no-param-reassign
                    item.disabled = (item.disableTradingDesk && userData.permissions.tradingDeskUser);
                    // eslint-disable-next-line no-param-reassign
                    item.permitted = !item.permissions || await authService.isAuthorized(item.permissions);
                }));
            }
            // filter list according to the permissions calculated above
            this.userMenu = userMenuFullList.map((group) => group.filter((item) => !item.disabled
                    && !(this.isMobile && item.hideOnMobile) && item.permitted));

            // filter out empty groups (all items in group are not permitted for the current user)
            this.userMenu = this.userMenu.filter((group) => !!group.length);

            // calculate contact us url according to the logged in user
            const contactUsItem = allMenuItems.find((item) => item.id === 'contactUs' && item.permitted);
            if (contactUsItem) {
                contactUsItem.path = this.contactUsUrl;
            }
        },
        resetTimerForDelay() {
            if (this.timeout && !this.isMobile) {
                clearTimeout(this.timeout);
                this.timeout = null;
            }
        },
        delayCloseMenu() {
            if (!this.timeout && !this.isMobile) {
                this.timeout = setTimeout(() => {
                    this.showUserNameMenu = false;
                    clearTimeout(this.timeout);
                    this.timeout = null;
                }, 2000);
            }
        }
    },
    async created() {
        if (userDataService.userDataLoaded()) {
            const userData = await userDataService.getUserData();
            this.contactUsUrl = await getContactUsUrl();
            this.currentUserId = userData.userId;
            await this.init(userData);
        }

        userDataService.subscribeUserDataChanges(async (mutation) => {
            if (mutation.payload) {
                const updatedUserData = await userDataService.getUserData();
                if (updatedUserData && this.currentUserId !== updatedUserData.userId) {
                    this.currentUserId = updatedUserData.userId;
                    this.contactUsUrl = await getContactUsUrl();
                }
                await this.init(updatedUserData);
            }
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
    }
};
</script>

<style scoped lang="scss">
@import "../common";

$userMenuMinWidth: 220px;
$userMenuMaxWidth: 250px;
$userMenuItemHeight: 32px;
$userMenuItemFontColor: $dark-300;

.mobile {
    .small-triangle {
        display: none;
    }

    .menu-container {
        z-index: 1;
        top: 0;
        position: relative;
        min-width: initial;
        max-width: initial;
        border: none;
        border-radius: 0;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.3);
    }

    .menu-item {
        background-color: $menuBackground;
        border: none;
        border-bottom: 1px solid $light-900;
        height: 50px;
        border-radius: 0;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.3);

        &:hover ,&.hover {
            background-color: $menuBackground;
        }

        a {
            color: $menuItemFontColor;
            line-height: 50px;
        }
        .menu-name{
            line-height: 18px;
            display: inline-block;

            &.shown-user-name{
                vertical-align: top;
                margin-top: 6px;
            }
        }
        .logout-icon {
            margin-left: 5px;
            margin-right: 15px;
            vertical-align: middle;
        }
        .my-account-icon {
            margin-left: 5px;
            position: relative;
            height: 15px;
            width: 15px;
            margin-right: 17px;
        }
    }

    // menu view
    .user-name {
        display: none;
    }

    // mobile view
    .menu-item .user-name {
        min-width: initial;
        max-width: initial;
        color: $light-800;
        display: block;
        @include font(6);
    }

    .user-icon {
        display: none;
    }
}

.in-menu-user-name {
    display: none;
    border-bottom: $border;
    color: $userMenuItemFontColor;
    padding: 11px 20px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.user-name {
        min-width: initial;
        max-width: initial;

        @media #{$desktopAndBelow} {
            display: block;
        }
        @media #{$mobile} {
            display: none;
        }
    }
}

.small-triangle {
    margin-top: 3px;
    vertical-align: top;
    display: inline-block;
    margin-left: 8px;

    &:after {
        border-top-color: $white;
    }

    @media #{$largeLaptopAndBelow} {
        display: none;
    }
}

.user-wrap {
    position: relative;
    display: inline-block;
    height: 100%;
    vertical-align: middle;
    padding: 0 24px 0 12px;
    cursor: pointer;
    margin-top: -3px; // for some reason there's a gap around user
    margin-right: 12px;

    &.mobile {
        border: none;
        width: 100%;
        padding: 0;
    }

    @media #{$largeLaptopAndBelow} {
        padding-top: 4px;
        margin-right: -4px;
    }
}

.menu-container {
    z-index: getZIndexLayerOffset(application,900);
    position: absolute;
    min-width: $userMenuMinWidth;
    max-width: $userMenuMaxWidth;
    width: 100%;
    background-color: $white;
    border-radius: $borderRadius;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.3);
    right: 0;
    top: 49px;
    line-height: 25px;

    @media #{$desktopAndAbove} {
        right: -12px;
    }

    @media #{$largeLaptopAndBelow} {
        top: 35px;
        right: 10px;
    }
    @media #{$mobile} {
        right: 0;
    }

    @media #{$tabletAndAbove} {
        .user-menu-item-space {
            padding: 4px 0;

        }

        .user-menu-item-list {
            padding-top: 4px;
            padding-bottom: 4px;
            border-bottom: $border;
        }
    }
}

.menu-item {
    background-color: $white;
    display: block;
    height: $userMenuItemHeight;
    width: 100%;

    &:hover, &.hover {
        background: $light-200;
    }

    a {
        @include font(5);
        color: $userMenuItemFontColor;
        line-height: $userMenuItemHeight;
        text-decoration:none;
        padding-left: 20px;
        display: block;
    }
}

.user-icon {
    margin: 3px 7px 0 0;
    vertical-align: top;

    @media #{$largeLaptopAndBelow} {
        margin: -5px 4px 0 0;
        width: 18px;
        height: 18px;
    }

    /deep/ span {
        vertical-align: middle;
    }
}

.user-name{
    min-width: 152px;
    max-width: 187px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: inline-block;

    @media #{$largeLaptopAndBelow} {
        display: none;
    }
}

</style>
